// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Display formatting for SOP governance. Kept out of the models and selectors:
// upstream sends `'GeneratorSetType'` and `'2.1'`, and only the view has an
// opinion about "Generator set" and "rev 2.1".

/**
 * Ontology entity type → human label: `'GeneratorSetType'` → `'Generator set'`.
 * The ontology ships no display names, so this is a mechanical de-camel-casing
 * of the type name rather than a lookup table that could go stale. Acronyms the
 * regex would otherwise split (`UPSType`, `ATSType`) survive because a run of
 * capitals is treated as one word.
 */
export function equipmentTypeLabel(entityType: string): string {
  const bare = entityType.replace(/Type$/, '');
  const words = bare.match(/[A-Z]+(?![a-z])|[A-Z][a-z]*|[0-9]+/g);
  if (!words || words.length === 0) return bare;
  return words
    .map((word, index) =>
      // Keep acronyms upper (UPS, ATS, PDU); lowercase trailing plain words so
      // the result reads as a phrase: "Generator set", not "Generator Set".
      word.length > 1 && word === word.toUpperCase()
        ? word
        : index === 0
          ? word
          : word.toLowerCase()
    )
    .join(' ');
}

/** SOP Factory `categories.equipment_type` is IRI-prefixed (`wattsch:UPSType`). */
export function stripOntologyPrefix(equipmentType: string): string {
  const colon = equipmentType.lastIndexOf(':');
  return colon === -1 ? equipmentType : equipmentType.slice(colon + 1);
}

/** `'2.1'` → `'rev 2.1'`; an absent version renders as an em dash. */
export function formatVersion(version: string): string {
  return version.trim() === '' ? '—' : `rev ${version}`;
}

/** `'in_review'` → `'In review'`. Upstream statuses are snake_case. */
export function formatStatus(status: string): string {
  if (status.trim() === '') return 'Unknown';
  const spaced = status.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
